import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { notificacoesAPI } from '../services/api';
import WebSocketService from '../services/websocket';

const timeAgo = (date) => {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'agora';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}sem`;
};

const iconConfig = {
  message: { badge: '✉️', color: 'bg-blue-500' },
  comment: { badge: '💬', color: 'bg-green-500' },
  mention: { badge: '📢', color: 'bg-purple-500' },
  system: { badge: '⚙️', color: 'bg-gray-500' },
};

const NotificationBell = ({ darkMode }) => {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchCount = useCallback(async () => {
    try {
      const res = await notificacoesAPI.contar();
      setCount(res.data.count);
    } catch {}
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificacoesAPI.listar(1, 20);
      setItems(res.data.items || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    if (WebSocketService.atendimentoSocket) {
      const handler = () => { fetchCount(); if (open) fetchItems(); };
      WebSocketService.atendimentoSocket.on('nova_notificacao', handler);
      return () => WebSocketService.atendimentoSocket?.off('nova_notificacao', handler);
    }
  }, [fetchCount, fetchItems, open]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    setOpen(!open);
    if (!open) fetchItems();
  };

  const handleMarkAllRead = async () => {
    await notificacoesAPI.marcarTodasLidas();
    setCount(0);
    setItems(prev => prev.map(i => ({ ...i, lida: true })));
  };

  const handleClickItem = async (item) => {
    if (!item.lida) {
      await notificacoesAPI.marcarLida(item.id);
      setCount(prev => Math.max(0, prev - 1));
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, lida: true } : i));
    }
    setOpen(false);
    if (item.tipo === 'message' && item.dados?.telefone) {
      navigate(`/conversas?telefone=${encodeURIComponent(item.dados.telefone)}${item.dados.instancia_id ? `&instancia=${item.dados.instancia_id}` : ''}`);
    } else if (item.tipo === 'comment' && item.dados?.media_id) {
      navigate(`/insights/publicacoes?post_media=${item.dados.media_id}&comment=${item.dados.comment_id || ''}`);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleOpen}
        className={`relative p-2 rounded-full transition-colors ${
          open
            ? darkMode ? 'bg-zinc-700 text-white' : 'bg-blue-100 text-blue-600'
            : darkMode ? 'hover:bg-zinc-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600'
        }`}
      >
        <Bell size={20} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 animate-pulse">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className={`fixed inset-0 top-[92px] sm:absolute sm:inset-auto sm:top-full sm:right-0 sm:mt-1 sm:w-96 sm:rounded-xl sm:max-h-[500px] shadow-2xl border overflow-hidden z-50 flex flex-col ${
          darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'
        }`}>
          {/* Header */}
          <div className={`flex items-center justify-between px-4 py-3 ${darkMode ? '' : ''}`}>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setOpen(false)}
                className={`sm:hidden p-1 rounded-md ${darkMode ? 'hover:bg-zinc-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <X size={20} />
              </button>
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Notificações</h3>
            </div>
            {count > 0 && (
              <button
                onClick={handleMarkAllRead}
                className={`text-xs font-medium px-2 py-1 rounded-md transition-colors ${
                  darkMode ? 'text-blue-400 hover:bg-zinc-700' : 'text-blue-600 hover:bg-blue-50'
                }`}
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          {/* Lista */}
          <div className="flex-1 overflow-y-auto sm:max-h-[420px]">
            {loading ? (
              <div className="p-6 text-center">
                <div className={`animate-spin w-6 h-6 border-2 rounded-full mx-auto ${
                  darkMode ? 'border-zinc-600 border-t-white' : 'border-gray-200 border-t-blue-500'
                }`} />
              </div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className={`mx-auto h-10 w-10 mb-2 ${darkMode ? 'text-zinc-600' : 'text-gray-300'}`} />
                <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Nenhuma notificação</p>
              </div>
            ) : (
              items.map(item => {
                const config = iconConfig[item.tipo] || iconConfig.system;
                return (
                  <div
                    key={item.id}
                    onClick={() => handleClickItem(item)}
                    className={`flex items-center gap-3 px-4 py-3.5 cursor-pointer transition-colors border-b ${
                      !item.lida
                        ? darkMode ? 'bg-zinc-700/40 hover:bg-zinc-700/70 border-zinc-700' : 'bg-blue-50/60 hover:bg-blue-50 border-gray-100'
                        : darkMode ? 'hover:bg-zinc-700/40 border-zinc-700' : 'hover:bg-gray-50 border-gray-100'
                    }`}
                  >
                    {/* Avatar + badge de tipo */}
                    <div className="relative flex-shrink-0">
                      {item.foto ? (
                        <img src={item.foto} alt="" className="w-12 h-12 rounded-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                      ) : null}
                      <div className={`w-12 h-12 rounded-full items-center justify-center text-lg font-semibold ${
                        darkMode ? 'bg-zinc-600 text-gray-100' : 'bg-gray-300 text-gray-700'
                      }`} style={{ display: item.foto ? 'none' : 'flex' }}>
                        {(() => {
                          const raw = item.nome_contato || item.titulo || '';
                          const nome = raw
                            .replace(/^(Nova mensagem de |Novo comentário de )/, '')
                            .replace(/^@?[\w.]+\s+enviou\s+\d+\s+mensagens?/, '')
                            .replace(/^\d+\s+mensagens?\s+de\s+/, '')
                            .replace(/^@/, '')
                            .trim();
                          const inicial = nome.charAt(0);
                          if (/[a-zA-ZÀ-ú]/.test(inicial)) return inicial.toUpperCase();
                          const match = raw.match(/@([\w])/);
                          return match ? match[1].toUpperCase() : config.badge;
                        })()}
                      </div>
                      {item.tipo !== 'message' && (
                      <div className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] border-2 ${
                        darkMode ? 'border-zinc-800' : 'border-white'
                      } ${config.color}`}>
                        <span>{config.badge}</span>
                      </div>
                      )}
                    </div>

                    {/* Conteúdo */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-[13px] leading-snug break-all ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        <span className="font-semibold">{item.titulo}</span>
                        {item.mensagem && (
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}> — {item.mensagem.length > 60 ? item.mensagem.slice(0, 60) + '…' : item.mensagem}</span>
                        )}
                      </p>
                      <p className={`text-xs mt-0.5 font-medium ${
                        !item.lida ? 'text-blue-500' : darkMode ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        {timeAgo(item.created_at)}
                        {item.dados?.count > 1 && (
                          <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> · {item.dados.count} mensagens</span>
                        )}
                      </p>
                    </div>

                    {/* Indicador não lida */}
                    {!item.lida && (
                      <div className="w-3 h-3 bg-blue-500 rounded-full flex-shrink-0" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
